import React, { useEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/core';
import { Box, Typography } from '@mui/material';
import { attachWordFlowObservers } from './wordPageLayout';
import {
  collectWordBlocks,
  layoutWordBlocks,
  paginateWordLines,
  ptToPx,
  type TextMeasurer,
  type WordGeometry,
  type WordLine,
  type WordPageBreak,
} from './wordLineBreaker';

interface WordPageBreakOverlayProps {
  editor: Editor | null;
  enabled: boolean;
  /** Fluss = Vuln-Header-Platzhalter + Editor-Body; hier wird das Word-Modell gebaut. */
  flowRoot: HTMLElement | null;
  measurer: TextMeasurer | null;
  geometry: WordGeometry;
  /** Steigt bei Font-Ladeereignissen → neu messen. */
  fontEpoch?: number;
}

interface BreakMarker {
  y: number;
  page: number;
  text: string;
  reason: WordPageBreak['reason'];
}

const sameMarkers = (a: BreakMarker[], b: BreakMarker[]): boolean =>
  a.length === b.length &&
  a.every((m, i) => Math.abs(m.y - b[i].y) < 0.5 && m.text === b[i].text && m.reason === b[i].reason);

/** Oberkante der Word-Zeile im DOM (px, relativ zu `root`). */
function lineTopPx(line: WordLine, root: HTMLElement): number | null {
  const rootRect = root.getBoundingClientRect();
  const rel = (top: number) => top - rootRect.top + root.scrollTop;
  const block = line.block;
  if (block.kind === 'box') {
    return block.dom ? rel(block.dom.getBoundingClientRect().top) : null;
  }
  const run = block.runs[line.startRun];
  const node = run?.node;
  if (node && node.parentNode) {
    const offset = Math.min(node.length, (run.nodeOffset ?? 0) + line.startOffset);
    const range = document.createRange();
    try {
      range.setStart(node, offset);
      range.setEnd(node, Math.min(node.length, offset + 1));
    } catch {
      return block.dom ? rel(block.dom.getBoundingClientRect().top) : null;
    }
    const rects = Array.from(range.getClientRects()).filter((r) => r.height > 0.5);
    const rect = rects[0];
    if (rect) {
      // Textfragmente melden die Schriftbox; auf die CSS-Zeilenbox erweitern.
      const host = (node.parentElement?.closest('p, li, h1, h2, h3, h4, h5, h6, pre, td, th, blockquote') ??
        node.parentElement) as HTMLElement | null;
      const lh = host ? parseFloat(getComputedStyle(host).lineHeight) : NaN;
      const pad = Number.isFinite(lh) && lh > rect.height ? (lh - rect.height) / 2 : 0;
      return rel(rect.top - pad);
    }
  }
  if (block.dom) {
    const domRect = block.dom.getBoundingClientRect();
    const lh = parseFloat(getComputedStyle(block.dom).lineHeight);
    const per = Number.isFinite(lh) ? lh : ptToPx(line.heightPt);
    return rel(domRect.top + line.lineIndex * per);
  }
  return null;
}

/**
 * Zeichnet Seitenumbrüche dort, wo Word den Report umbricht: Fluss →
 * Word-Blöcke → Zeilen (Word-Umbruchregeln, Blocksatz-Stauchung) →
 * Paginierung (Witwen/Waisen) → Marker vor dem ersten Wort der neuen Seite.
 */
const WordPageBreakOverlay: React.FC<WordPageBreakOverlayProps> = ({
  editor,
  enabled,
  flowRoot,
  measurer,
  geometry,
  fontEpoch = 0,
}) => {
  const [markers, setMarkers] = useState<BreakMarker[]>([]);
  const [pageCount, setPageCount] = useState(1);
  const markersRef = useRef<BreakMarker[]>([]);

  useEffect(() => {
    if (!enabled || !editor || !flowRoot || !measurer) {
      markersRef.current = [];
      setMarkers([]);
      setPageCount(1);
      return undefined;
    }

    let raf = 0;
    const apply = (next: BreakMarker[], pages: number) => {
      setPageCount(pages);
      if (sameMarkers(markersRef.current, next)) return;
      markersRef.current = next;
      setMarkers(next);
    };

    const measure = () => {
      const blocks = collectWordBlocks(flowRoot, { geometry, editorDom: true, measurerForTables: measurer });
      const lines = layoutWordBlocks(blocks, measurer, geometry);
      const pagination = paginateWordLines(lines, geometry.pagePt.contentHeight);
      if (process.env.NODE_ENV !== 'production') {
        // Diagnose im Dev-Modus: window.__starkWordLines zeigt das Word-Zeilenmodell.
        (window as unknown as { __starkWordLines?: unknown }).__starkWordLines = lines.map((l) => ({
          block: l.block.label,
          text: l.text,
          heightPt: l.heightPt,
          afterPt: l.afterPt,
          naturalWidthPt: Math.round(l.naturalWidthPt * 100) / 100,
        }));
      }
      const next: BreakMarker[] = [];
      pagination.breaks.forEach((b, i) => {
        const line = lines[b.lineIndex];
        const y = lineTopPx(line, flowRoot);
        if (y == null) return;
        next.push({ y, page: i + 2, text: line.text.trim().slice(0, 60), reason: b.reason });
      });
      apply(next, pagination.pageCount);
    };

    const schedule = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        raf = 0;
        measure();
      });
    };

    schedule();
    const stopObserve = attachWordFlowObservers(flowRoot, schedule);
    editor.on('update', schedule);
    const boot = window.setTimeout(schedule, 120);
    window.addEventListener('resize', schedule);

    return () => {
      window.clearTimeout(boot);
      if (raf) cancelAnimationFrame(raf);
      stopObserve();
      editor.off('update', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [editor, enabled, flowRoot, measurer, geometry, fontEpoch]);

  if (!enabled) return null;

  return (
    <Box
      data-testid="word-page-break-overlay"
      data-page-count={pageCount}
      aria-hidden
      sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1 }}
    >
      {markers.map((m, i) => (
        <Box
          key={`pb-${i}-${Math.round(m.y)}`}
          data-testid={`word-page-break-${i + 1}`}
          data-reason={m.reason}
          data-manual={m.reason === 'manual' ? 'true' : undefined}
          title={
            m.reason === 'manual'
              ? `Manueller Umbruch: Seite ${m.page} beginnt mit „${m.text}“`
              : `Word: Seite ${m.page} beginnt mit „${m.text}“`
          }
          sx={{
            position: 'absolute',
            top: m.y,
            left: -12,
            right: -12,
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box sx={{ flex: 1, borderTop: '2px dashed #c45c26' }} />
          <Typography
            variant="caption"
            sx={{
              flexShrink: 0,
              px: 0.75,
              py: 0.15,
              bgcolor: '#c45c26',
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.65rem',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              borderRadius: 0.5,
              lineHeight: 1.4,
            }}
          >
            {m.reason === 'manual' ? 'Manueller Umbruch' : 'Seitenumbruch'} · Seite {m.page - 1} → {m.page}
          </Typography>
        </Box>
      ))}
      <Typography
        variant="caption"
        data-testid="word-page-count"
        sx={{
          position: 'absolute',
          top: 4,
          right: 0,
          color: 'rgba(180, 196, 214, 0.85)',
          fontWeight: 600,
          fontSize: '0.65rem',
        }}
      >
        {pageCount} {pageCount === 1 ? 'Seite' : 'Seiten'} · A4 wie Word-Export
      </Typography>
    </Box>
  );
};

export default WordPageBreakOverlay;
